// components/ui/AlertDialog.tsx — Destructive confirmation dialog

import * as RadixAlertDialog from '@radix-ui/react-alert-dialog';
import { Button } from './Button';
import styles from './AlertDialog.module.css';

interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  /** Whether the confirm action is destructive (red button) */
  isDestructive?: boolean;
  isLoading?: boolean;
}

/**
 * AlertDialog — accessible confirmation dialog for irreversible actions.
 * Wraps Radix AlertDialog with Folk Of Interest design system styles.
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  isDestructive = false,
  isLoading = false,
}: AlertDialogProps) {
  return (
    <RadixAlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixAlertDialog.Portal>
        <RadixAlertDialog.Overlay className={styles.overlay} />
        <RadixAlertDialog.Content className={styles.content} asChild>
          <div>
            <RadixAlertDialog.Title className={styles.title}>
              {title}
            </RadixAlertDialog.Title>
            <RadixAlertDialog.Description className={styles.description}>
              {description}
            </RadixAlertDialog.Description>

            <div className={styles.actions}>
              <RadixAlertDialog.Cancel asChild>
                <Button variant="ghost" size="md" disabled={isLoading}>
                  {cancelLabel}
                </Button>
              </RadixAlertDialog.Cancel>

              <RadixAlertDialog.Action asChild>
                <Button
                  variant={isDestructive ? 'destructive' : 'primary'}
                  size="md"
                  isLoading={isLoading}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </Button>
              </RadixAlertDialog.Action>
            </div>
          </div>
        </RadixAlertDialog.Content>
      </RadixAlertDialog.Portal>
    </RadixAlertDialog.Root>
  );
}
